import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  CATALOG_QUERY_POLICY,
  deviceCatalogKeys,
  invalidateDeviceCatalog,
} from '@/features/device-models/keys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ArrowLeft, Lock, Unlock, Box } from '@/lib/icons';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ActivityPanel,
  useUnresolvedActivityCount,
} from '@/features/inventory/components/unit-detail/ActivityPanel';
import { ContractPanel } from '@/features/inventory/components/unit-detail/ContractPanel';
import { QuickActions } from '@/features/inventory/components/unit-detail/QuickActions';
import { DeviceActionsPanel } from '@/features/inventory/components/unit-detail/DeviceActionsPanel';

import {
  getDeviceUnitById,
  updateDeviceUnit,
} from '@/features/inventory/api';
import {
  isEffectivelyLocked,
  hasLockDrift,
} from '@/features/inventory/device-unit';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/store/hooks';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RecordLink } from '@/components/ui/record-link';
import EditableTextField from '@/components/ui/editable-text-field';
import { LinkedTicketsPanel } from '@/features/support/LinkedTicketsPanel';
import {
  DeviceFeedbackTab,
  useSupportsCustomerFeedback,
} from '@/features/feedback/components/DeviceFeedbackTab';
import { hasPermission } from '@/lib/auth/permissions';
import { formatDate } from '@/lib/dates';
import { RelativeTime } from '@/components/common/relative-time';
import { OdooLinkBadge } from '@/features/odoo/components/OdooLinkBadge';

const tabSchema = z.object({
  tab: z.enum(['overview', 'activity', 'contract', 'feedback']).optional(),
});

export const Route = createFileRoute('/_layout/inventory/units/$unitId')({
  component: DeviceUnitDetailPage,
  validateSearch: tabSchema,
});

const inventoryStatusBadge = (status: string) => {
  const map: Record<string, { cls: string; label: string }> = {
    procured: {
      cls: 'bg-slate-500/15 text-slate-600 border-slate-500/30 dark:text-slate-400',
      label: 'Procured',
    },
    in_stock: {
      cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400',
      label: 'In Stock',
    },
    reserved: {
      cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30 dark:text-amber-400',
      label: 'Reserved',
    },
    financed: {
      cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400',
      label: 'Financed',
    },
    returned: {
      cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30 dark:text-violet-400',
      label: 'Returned',
    },
    damaged: {
      cls: 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400',
      label: 'Damaged',
    },
    written_off: {
      cls: 'bg-gray-500/15 text-gray-500 border-gray-500/30 dark:text-gray-400',
      label: 'Written Off',
    },
  };
  const s = map[status] ?? { cls: '', label: status };
  return <Badge className={s.cls}>{s.label}</Badge>;
};

function DeviceUnitDetailPage() {
  const { unitId } = Route.useParams();
  const search = Route.useSearch();
  const requestedTab = search.tab ?? 'overview';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';
  const unresolvedCount = useUnresolvedActivityCount(unitId);

  // Data-driven, not name-driven: the tab exists only where the resolved
  // provider declares it can serve end-user feedback. A software-fallback
  // unit — or Knox before its adapter lands — gets no tab at all, rather than
  // an empty one that looks broken.
  const user = useAppSelector((state) => state.auth.user);
  const { supported: supportsFeedback } = useSupportsCustomerFeedback(unitId);
  const canReadFeedback = hasPermission(user, 'feedback', 'read');
  const showFeedbackTab = supportsFeedback && canReadFeedback;
  // A bookmarked ?tab=feedback on a unit that no longer qualifies would
  // otherwise select a tab with no trigger and no content — a blank page with
  // nothing to click back from.
  const activeTab =
    requestedTab === 'feedback' && !showFeedbackTab ? 'overview' : requestedTab;

  const { data, isLoading } = useQuery({
    queryKey: deviceCatalogKeys.unit(unitId),
    queryFn: () => getDeviceUnitById(unitId),
    ...CATALOG_QUERY_POLICY,
  });

  const invalidateUnit = () => invalidateDeviceCatalog(queryClient);

  const handleFieldSave = async (field: string, value: string | number) => {
    const result = await updateDeviceUnit(unitId, { [field]: value });
    if (!result.succeeded) {
      throw new Error(
        result.errors?.[0] ?? t('inventory.toast.unitUpdateFailed')
      );
    }
    invalidateUnit();
    toast({
      title: t('common.success'),
      description: t('inventory.toast.unitUpdated'),
    });
  };

  const unit = data?.value;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="lg:col-span-2 h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{t('common.error')}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate({ to: '/inventory/units' })}
        >
          {t('common.actions.back')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/inventory/units' })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800">
            <Box className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {unit.brand ?? ''} {unit.modelName ?? ''}
            </h1>
            <p className="text-muted-foreground text-sm font-mono">
              IMEI: {unit.imei}
            </p>
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {inventoryStatusBadge(unit.inventoryStatus)}
          {isEffectivelyLocked(unit) ? (
            <Badge className="bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400">
              Locked
            </Badge>
          ) : (
            <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
              Unlocked
            </Badge>
          )}
          {hasLockDrift(unit) && (
            <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-400">
              Sync pending
            </Badge>
          )}
          <OdooLinkBadge
            subjectType="device_unit"
            subjectId={unit.id}
            label={t('odoo.badge.serialLot')}
            showNotTracked
          />
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            to: '.',
            search: (prev: Record<string, unknown>) => ({
              ...prev,
              tab: v === 'overview' ? undefined : v,
            }),
            replace: true,
          })
        }
      >
        <TabsList
          className={`grid w-full max-w-md ${
            showFeedbackTab ? 'grid-cols-4' : 'grid-cols-3'
          }`}
        >
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity" className="gap-2">
            Activity
            {unresolvedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-[11px] font-bold border border-amber-500/35">
                {unresolvedCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="contract">Contract</TabsTrigger>
          {showFeedbackTab && (
            <TabsTrigger value="feedback">{t('feedback.tab')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          {/* Main layout */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left: Unit details */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('inventory.unitDetail.deviceInfo')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow
                      label="IMEI"
                      value={<span className="font-mono">{unit.imei}</span>}
                    />
                    <EditableTextField
                      variant="compact"
                      label="Serial Number"
                      value={unit.serialNumber ?? ''}
                      onSave={(v) => handleFieldSave('serialNumber', v)}
                      placeholder="-"
                    />
                    <InfoRow label="Brand" value={unit.brand ?? '-'} />
                    <InfoRow
                      label="Model"
                      value={
                        unit.deviceModelId ? (
                          <RecordLink
                            to="/device-models/$modelId"
                            params={{ modelId: unit.deviceModelId }}
                            title={unit.modelName ?? undefined}
                            aria-label={`View device model ${unit.modelName ?? ''}`}
                          >
                            {unit.modelName ?? '-'}
                          </RecordLink>
                        ) : (
                          (unit.modelName ?? '-')
                        )
                      }
                    />
                    <InfoRow
                      label="Variant"
                      value={
                        [unit.storageCapacity, unit.color]
                          .filter(Boolean)
                          .join(' / ') || '-'
                      }
                    />
                    <InfoRow
                      label="Condition"
                      value={
                        <Badge
                          className={
                            unit.condition === 'new'
                              ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                              : unit.condition === 'refurbished'
                                ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
                                : 'bg-red-500/15 text-red-600 border-red-500/30'
                          }
                        >
                          {unit.condition}
                        </Badge>
                      }
                    />
                    <InfoRow
                      label="Purchase Price"
                      value={formatCurrency(
                        unit.effectivePurchasePrice,
                        currency
                      )}
                    />
                    <InfoRow
                      label="Retail Price"
                      value={
                        <span className="font-bold">
                          {formatCurrency(unit.effectiveRetailPrice, currency)}
                        </span>
                      }
                    />
                    <InfoRow
                      label="Enrolled"
                      value={formatDate(unit.enrolledAt)}
                    />
                    <EditableTextField
                      variant="compact"
                      type="multiline"
                      label="Notes"
                      value={unit.notes ?? ''}
                      onSave={(v) => handleFieldSave('notes', v)}
                      placeholder="-"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right sidebar: Actions */}
            <div className="space-y-6">
              {/* Lock Status — intent + device-reported drift indicator */}
              <Card>
                <CardHeader>
                  <CardTitle>Lock Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-center py-2">
                    {isEffectivelyLocked(unit) ? (
                      <>
                        <div className="mx-auto w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mb-2">
                          <Lock className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                        <p className="font-semibold text-red-600 dark:text-red-400">
                          Device Locked
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mb-2">
                          <Unlock className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <p className="font-semibold text-emerald-600 dark:text-emerald-400">
                          Device Unlocked
                        </p>
                      </>
                    )}
                  </div>
                  {unit.deviceLockStatus &&
                    unit.deviceLockStatus !== unit.lockStatus && (
                      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                        <p className="font-medium">
                          Device hasn&apos;t acknowledged yet
                        </p>
                        <p className="mt-0.5 text-amber-700/80 dark:text-amber-400/80">
                          Handset last reported{' '}
                          <span className="font-semibold">
                            {unit.deviceLockStatus}
                          </span>
                          {unit.deviceLockReportedAt && (
                            <>
                              {' ('}
                              <RelativeTime value={unit.deviceLockReportedAt} />
                              {')'}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Actions — single section: common actions (QuickActions)
                  first, then the device-bound, catalog-driven provider
                  actions (lock / unlock / remove-lock). */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('inventory.unitDetail.actions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <QuickActions unit={unit} />
                  <DeviceActionsPanel unitId={unit.id} />
                </CardContent>
              </Card>

              {/* Quick Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <InfoRow
                    label="Status"
                    value={inventoryStatusBadge(unit.inventoryStatus)}
                  />
                  <InfoRow
                    label="Retail Price"
                    value={
                      <span className="font-semibold">
                        {formatCurrency(unit.effectiveRetailPrice, currency)}
                      </span>
                    }
                  />
                  <InfoRow
                    label="Purchase Price"
                    value={formatCurrency(
                      unit.effectivePurchasePrice,
                      currency
                    )}
                  />
                  <InfoRow
                    label="Margin"
                    value={
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(
                          unit.effectiveRetailPrice -
                            unit.effectivePurchasePrice,
                          currency
                        )}
                      </span>
                    }
                  />
                </CardContent>
              </Card>

              {/* Backlog §7: support tickets linked to this device. Deciding
                  whether to unlock a handset should not require checking
                  another tab for an open dispute. */}
              <LinkedTicketsPanel deviceUnitId={unit.id} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <ActivityPanel unitId={unitId} />
        </TabsContent>

        <TabsContent value="contract">
          <ContractPanel unit={unit} />
        </TabsContent>

        {showFeedbackTab && (
          <TabsContent value="feedback">
            <DeviceFeedbackTab
              unitId={unit.id}
              canTriage={hasPermission(user, 'feedback', 'update')}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function InfoRow({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}
